import React, { useState } from 'react';
import { Logo } from './Logo';
import { SiteSettings, LanguageCode } from '../types';
import { LANGUAGES, translations } from '../i18n/translations';
import { Search, Bell, Menu, ShieldCheck, User, Globe, Lock, Heart, Ticket } from 'lucide-react';

interface HeaderProps {
  currentLang: LanguageCode;
  onLanguageChange: (lang: LanguageCode) => void;
  activeTab?: string;
  currentTab?: string;
  onNavClick: (tab: string) => void;
  onToggleDrawer?: () => void;
  onOpenDrawer?: () => void;
  onOpenSearch?: () => void;
  unreadNotificationsCount?: number;
  isAdminMode?: boolean;
  onToggleAdmin?: () => void;
  isLoggedIn?: boolean;
  onOpenAuthModal?: () => void;
  onOpenAuth?: () => void;
  settings?: SiteSettings;
}

export const Header: React.FC<HeaderProps> = ({
  currentLang,
  onLanguageChange,
  activeTab,
  currentTab,
  onNavClick,
  onToggleDrawer,
  onOpenDrawer,
  onOpenSearch,
  unreadNotificationsCount = 2,
  isAdminMode = false,
  onToggleAdmin,
  isLoggedIn = false,
  onOpenAuthModal,
  onOpenAuth,
  settings,
}) => {
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const t = translations[currentLang];

  const currentActiveTab = activeTab || currentTab || 'home';
  const handleToggleDrawer = onToggleDrawer || onOpenDrawer || (() => {});
  const handleOpenAuth = onOpenAuthModal || onOpenAuth || (() => {});
  const handleToggleAdmin = onToggleAdmin || (() => onNavClick('admin'));

  const mainNavItems = [
    { id: 'home', label: t.home },
    { id: 'about', label: t.aboutUs },
    { id: 'activities', label: t.activities },
    { id: 'news', label: t.news },
    { id: 'events', label: t.events },
    { id: 'culture', label: t.culture },
    { id: 'ads', label: t.ads || 'REKLAMLAR' },
    { id: 'membership', label: t.membership },
    { id: 'donate', label: t.donate },
    { id: 'documents', label: t.documents },
    { id: 'contact', label: t.contact },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 h-[76px] sm:h-[86px] z-40 glass-header px-2.5 sm:px-6 flex items-center justify-between gap-1.5 sm:gap-4 select-none transition-all duration-300">
      {/* Left: Logo and Title Area */}
      <div 
        className="cursor-pointer flex items-center gap-1.5 sm:gap-2.5 flex-1 min-w-0 pr-1 py-1"
        onClick={() => onNavClick('home')}
      >
        <Logo
          size={52}
          showText={true}
          customLogoUrl={settings?.logoImageUrl}
          siteName={settings?.siteName}
          logoText1={settings?.logoText1}
          logoText2={settings?.logoText2}
          logoText3={settings?.logoText3}
        />
      </div>

      {/* Desktop Main Navigation Links (Visible on xl screens 1280px+) */}
      <nav className="hidden xl:flex items-center gap-1.5 bg-[#0B1426]/90 p-1 rounded-2xl border border-[#38BDF8]/30 flex-shrink-0 shadow-lg">
        {mainNavItems.slice(0, 7).map((item) => (
          <button
            key={item.id}
            onClick={() => onNavClick(item.id)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold tracking-wide transition-all duration-300 ${
              currentActiveTab === item.id
                ? 'bg-[#38BDF8] text-[#0B1426] border border-[#A5F3FC] font-extrabold shadow-[0_0_15px_rgba(56,189,248,0.7)] scale-105'
                : 'bg-[#132238] text-gray-200 border border-[#38BDF8]/30 hover:border-[#38BDF8] hover:text-[#38BDF8] hover:shadow-[0_0_12px_rgba(56,189,248,0.4)] active:scale-95'
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* Center/Right Actions: Language, Admin Toggle, Bell, Profile, Hamburger */}
      <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">

        {/* Language Selector (Text only) */}
        <div className="relative">
          <button
            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-xl bg-[#132238] border border-[#38BDF8]/40 hover:border-[#38BDF8] text-xs font-bold text-[#7DD3FC] hover:text-white hover:shadow-[0_0_12px_rgba(56,189,248,0.5)] active:scale-95 transition-all cursor-pointer"
          >
            <Globe className="w-3.5 h-3.5 text-[#38BDF8]" />
            <span>{currentLang}</span>
          </button>

          {langDropdownOpen && (
            <div 
              className="absolute right-0 mt-2 w-36 glass-card rounded-xl py-1 z-50 border border-[#38BDF8]/40 shadow-2xl bg-[#0B1426]/95 animate-in fade-in zoom-in-95 duration-150"
              onClick={() => setLangDropdownOpen(false)}
            >
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => onLanguageChange(lang.code)}
                  className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors flex items-center justify-between ${
                    currentLang === lang.code
                      ? 'bg-[#38BDF8] text-[#0B1426] font-bold shadow-[0_0_10px_rgba(56,189,248,0.5)]'
                      : 'text-gray-200 hover:bg-[#38BDF8]/20 hover:text-[#38BDF8]'
                  }`}
                >
                  <span>{lang.label}</span>
                  <span className="text-[10px] text-[#7DD3FC] font-mono">[{lang.code}]</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications Bell */}
        <button
          onClick={() => onNavClick('member-panel')}
          className="relative p-2 sm:p-2.5 rounded-xl bg-[#132238] border border-[#38BDF8]/40 text-[#7DD3FC] hover:text-white hover:border-[#38BDF8] hover:shadow-[0_0_12px_rgba(56,189,248,0.5)] active:scale-95 transition-all cursor-pointer"
          title={t.notifications}
        >
          <Bell className="w-4 h-4 text-[#38BDF8]" />
          {unreadNotificationsCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#38BDF8] text-[#0B1426] text-[10px] font-black flex items-center justify-center border border-[#0B1426] shadow-[0_0_8px_rgba(56,189,248,0.8)]">
              {unreadNotificationsCount}
            </span>
          )}
        </button>

        {/* Online Ticket Store Button */}
        <button
          onClick={() => onNavClick('tickets')}
          className="flex items-center gap-1 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg bg-[#38BDF8] hover:bg-[#7DD3FC] text-[#0B1426] font-bold text-xs border border-[#7DD3FC]/50 active:scale-95 transition-all cursor-pointer shadow-sm"
          title="Online QR Kodlu Bilet Satın Al"
        >
          <Ticket className="w-3.5 h-3.5 text-[#0B1426]" />
          <span className="hidden xs:inline sm:inline text-xs font-black uppercase">🎫 Bilet Al</span>
        </button>

        {/* Red Donation Button for Members */}
        {isLoggedIn && (
          <button
            onClick={() => onNavClick('donate')}
            className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-gradient-to-r from-red-600 via-rose-600 to-red-600 hover:from-red-500 hover:to-rose-500 text-white font-extrabold text-xs sm:text-sm border border-red-400 shadow-[0_0_18px_rgba(239,68,68,0.8)] active:scale-95 transition-all cursor-pointer animate-pulse"
            title="Bağış Yap / Destek Ol"
          >
            <Heart className="w-4 h-4 fill-white text-white" />
            <span className="hidden xs:inline sm:inline">❤️ BAĞIŞ YAP</span>
          </button>
        )}

        {/* Profile / Auth Quick Button */}
        <button
          onClick={() => (isLoggedIn ? onNavClick('member-panel') : handleOpenAuth())}
          className="p-2 sm:p-2.5 rounded-xl bg-[#132238] border border-[#38BDF8]/50 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:border-[#A5F3FC] hover:shadow-[0_0_15px_rgba(56,189,248,0.6)] active:scale-95 transition-all cursor-pointer"
          title={isLoggedIn ? t.profile : t.login}
        >
          <User className="w-4 h-4" />
        </button>

        {/* Hamburger Menu Trigger */}
        <button
          onClick={handleToggleDrawer}
          className="w-10 h-10 sm:w-[48px] sm:h-[48px] rounded-xl bg-[#132238] border border-[#38BDF8]/50 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:border-[#A5F3FC] hover:shadow-[0_0_20px_rgba(56,189,248,0.7)] flex items-center justify-center transition-all duration-300 shadow-md active:scale-95 cursor-pointer"
          aria-label="Open Menu"
        >
          <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>
    </header>
  );
};
